import { prepareNativeDocumentResources, releaseNativeDocumentResources } from '../public-format/native-resources';
import { throwIfAborted } from '../codecs/types';
import { createId } from '../core/model';
import type { DocumentSession } from '../core/session';
import { deserializeProject, serializeProject } from './project-format';

const DATABASE = 'maipo-recovery';
const VERSION = 2;
const SNAPSHOTS = 'snapshots-v2';
const META = 'meta-v2';
const MAX_RECOVERY_BYTES = 512 * 1024 * 1024;
const MAX_RECOVERY_RECORDS = 64;

interface RecoveryRecord {
  key: string;
  sessionId: string;
  documentId: string;
  documentName: string;
  revision: number;
  savedAt: string;
  lastAccessedAt: string;
  bytes: Uint8Array;
}

interface RecoveryMeta {
  sessionId: string;
  documentId: string;
  currentKey: string;
  currentRevision: number;
  previousKey?: string;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted.'));
  });
}

async function withWriteTransaction<T>(transaction: IDBTransaction, work: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  // Observe completion immediately, including aborts that happen while work is
  // waiting on a request. Keep the outcome fulfilled to avoid an unhandled rejection.
  const completion = transactionDone(transaction).then(
    () => ({ ok: true as const }),
    (error: unknown) => ({ ok: false as const, error }),
  );
  const abort = () => {
    try { transaction.abort(); } catch { /* The transaction may already be finished. */ }
  };
  signal?.addEventListener('abort', abort, { once: true });
  try {
    if (signal?.aborted) throw new DOMException('Recovery was cancelled.', 'AbortError');
    const result = await work();
    const outcome = await completion;
    if (!outcome.ok) throw outcome.error;
    return result;
  } catch (error) {
    // A synchronous put/delete failure does not itself roll back prior requests.
    abort();
    await completion;
    if (signal?.aborted) throw new DOMException('Recovery was cancelled.', 'AbortError');
    throw error;
  } finally {
    signal?.removeEventListener('abort', abort);
  }
}

async function openDatabase(): Promise<IDBDatabase> {
  const request = indexedDB.open(DATABASE, VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(SNAPSHOTS)) database.createObjectStore(SNAPSHOTS, { keyPath: 'key' });
    if (!database.objectStoreNames.contains(META)) database.createObjectStore(META, { keyPath: 'sessionId' });
  };
  return requestResult(request);
}

export interface RecoverySummary {
  sessionId: string;
  documentId: string;
  documentName: string;
  revision: number;
  savedAt: string;
  key: string;
  generation: 'current' | 'previous';
}

export interface RecoverySaveResult extends RecoverySummary {
  committed: boolean;
}

async function enforceRecoveryBudget(transaction: IDBTransaction, protectedSessionId: string): Promise<void> {
  // Runs inside the save transaction, including its pending snapshot and pointer.
  const records = await requestResult(transaction.objectStore(SNAPSHOTS).getAll()) as RecoveryRecord[];
  const metadata = await requestResult(transaction.objectStore(META).getAll()) as RecoveryMeta[];
  const protectedKeys = new Set(metadata.flatMap((item) => [item.currentKey, ...(item.previousKey ? [item.previousKey] : [])]));
  const ordered = [...records].sort((a, b) => a.lastAccessedAt.localeCompare(b.lastAccessedAt));
  let bytes = records.reduce((sum, item) => sum + item.bytes.byteLength, 0);
  let count = records.length;
  const remove: string[] = [];
  for (const item of ordered) {
    if (bytes <= MAX_RECOVERY_BYTES && count <= MAX_RECOVERY_RECORDS) break;
    if (protectedKeys.has(item.key)) continue;
    remove.push(item.key);
    bytes -= item.bytes.byteLength;
    count -= 1;
  }
  const metaUpdates: RecoveryMeta[] = [];
  const metaDeletes: string[] = [];
  const recordByKey = new Map(records.map((item) => [item.key, item]));
  for (const meta of [...metadata].sort((a, b) => (recordByKey.get(a.previousKey ?? '')?.lastAccessedAt ?? '').localeCompare(recordByKey.get(b.previousKey ?? '')?.lastAccessedAt ?? ''))) {
    if (bytes <= MAX_RECOVERY_BYTES && count <= MAX_RECOVERY_RECORDS) break;
    if (!meta.previousKey || meta.sessionId.startsWith(CHECKPOINT_SLOT_PREFIX)) continue;
    const previous = recordByKey.get(meta.previousKey);
    if (!previous || remove.includes(previous.key)) continue;
    remove.push(previous.key);
    bytes -= previous.bytes.byteLength;
    count -= 1;
    const updated = { ...meta };
    delete updated.previousKey;
    metaUpdates.push(updated);
  }
  for (const meta of [...metadata].sort((a, b) => (recordByKey.get(a.currentKey)?.lastAccessedAt ?? '').localeCompare(recordByKey.get(b.currentKey)?.lastAccessedAt ?? ''))) {
    if (bytes <= MAX_RECOVERY_BYTES && count <= MAX_RECOVERY_RECORDS) break;
    if (meta.sessionId === protectedSessionId || meta.sessionId.startsWith(CHECKPOINT_SLOT_PREFIX)) continue;
    const current = recordByKey.get(meta.currentKey);
    if (current && !remove.includes(current.key)) { remove.push(current.key); bytes -= current.bytes.byteLength; count -= 1; }
    if (meta.previousKey) {
      const previous = recordByKey.get(meta.previousKey);
      if (previous && !remove.includes(previous.key)) { remove.push(previous.key); bytes -= previous.bytes.byteLength; count -= 1; }
    }
    metaDeletes.push(meta.sessionId);
  }
  if (remove.length || metaUpdates.length || metaDeletes.length) {
    const cleanup = transaction;
    for (const key of remove) cleanup.objectStore(SNAPSHOTS).delete(key);
    for (const meta of metaUpdates) if (!metaDeletes.includes(meta.sessionId)) cleanup.objectStore(META).put(meta);
    for (const sessionId of metaDeletes) cleanup.objectStore(META).delete(sessionId);
  }
}

export async function saveRecovery(session: DocumentSession, signal?: AbortSignal): Promise<RecoverySaveResult> {
  return writeRecovery(session, undefined, signal);
}

export const CHECKPOINT_SLOT_PREFIX = 'checkpoint:';

/**
 * A named restore point that the session's own autosaves never replace
 * (ADR-009 §6.4 "Before agent changes"). It is listed and restored like any
 * recovery and outlives history eviction and a tab crash. Two slots per
 * session: `first` (the state before any agent change; written once and
 * never replaced) and `latest` (the periodic checkpoint, rolling). Budget
 * enforcement never evicts checkpoint slots (review finding 9). It does not
 * change the session's autosave status.
 */
export async function saveRecoveryCheckpoint(session: DocumentSession, label: string, slot: 'first' | 'latest' = 'first', signal?: AbortSignal): Promise<RecoverySaveResult> {
  return writeRecovery(session, { slot: `${CHECKPOINT_SLOT_PREFIX}${slot}:${session.sessionId}`, label }, signal);
}

async function writeRecovery(session: DocumentSession, checkpoint: { slot: string; label: string } | undefined, signal?: AbortSignal): Promise<RecoverySaveResult> {
  const startedAt = performance.now();
  const captured = session.captureRevision();
  const slot = checkpoint?.slot ?? captured.sessionId;
  const tracked = !checkpoint;
  const callerSignal = signal;
  const controller = new AbortController();
  const abort = () => controller.abort();
  callerSignal?.addEventListener('abort', abort, { once: true });
  session.closedSignal.addEventListener('abort', abort, { once: true });
  if (callerSignal?.aborted || session.closedSignal.aborted) abort();
  signal = controller.signal;
  const documentName = checkpoint ? `${captured.document.name} — ${checkpoint.label}` : captured.document.name;
  if (tracked) session.recovery = { revision: captured.revision, status: 'saving' };
  try {
    const bytes = await serializeProject(captured.document, signal);
    if (bytes.byteLength > MAX_RECOVERY_BYTES) throw new Error('This document exceeds the local recovery byte budget. Save it to a durable file.');
    // A generation is never made current until the bytes pass the same strict reader used by Open.
    await deserializeProject(bytes, signal);
    const database = await openDatabase();
    try {
      const savedAt = new Date().toISOString();
      const key = `${slot}:${captured.revision}:${createId('recovery')}`;
      const record: RecoveryRecord = {
        key,
        sessionId: slot,
        documentId: captured.document.id,
        documentName,
        revision: captured.revision,
        savedAt,
        lastAccessedAt: savedAt,
        bytes,
      };
      if (signal?.aborted) throw new DOMException('Recovery was cancelled.', 'AbortError');
      // Commit the validated archive and its generation pointer atomically.
      const point = database.transaction([META, SNAPSHOTS], 'readwrite');
      const { committed, oldMeta } = await withWriteTransaction(point, async () => {
        const metadataStore = point.objectStore(META);
        const snapshots = point.objectStore(SNAPSHOTS);
        const oldMeta = await requestResult(metadataStore.get(slot)) as RecoveryMeta | undefined;
        if (signal?.aborted) throw new DOMException('Recovery was cancelled.', 'AbortError');
        const committed = !oldMeta || captured.revision >= oldMeta.currentRevision;
        if (committed) {
          const sameRevision = oldMeta?.currentRevision === captured.revision;
          const previousKey = sameRevision ? oldMeta.previousKey : oldMeta?.currentKey;
          snapshots.put(record);
          const meta: RecoveryMeta = {
            sessionId: slot,
            documentId: captured.document.id,
            currentKey: key,
            currentRevision: captured.revision,
            ...(previousKey && (!sameRevision || previousKey !== oldMeta?.currentKey) ? { previousKey } : {}),
          };
          metadataStore.put(meta);
          // Refreshing the same revision must not evict the last distinct edit.
          const obsoleteKey = sameRevision ? oldMeta.currentKey : oldMeta?.previousKey;
          if (obsoleteKey && obsoleteKey !== meta.previousKey) snapshots.delete(obsoleteKey);
          await enforceRecoveryBudget(point, slot);
        }
        return { committed, oldMeta };
      }, signal);
      if (tracked && committed && captured.revision >= session.recovery.revision) session.recovery = { revision: captured.revision, status: 'saved', savedAt, durationMs: performance.now() - startedAt };
      return { sessionId: slot, documentId: captured.document.id, documentName, revision: captured.revision, savedAt, key: committed ? key : oldMeta?.currentKey ?? key, generation: 'current', committed };
    } finally {
      database.close();
    }
  } catch (error) {
    if (tracked && captured.revision >= session.recovery.revision) session.recovery = { revision: captured.revision, status: 'failed', message: error instanceof Error ? error.message : 'Recovery failed.', durationMs: performance.now() - startedAt };
    throw error;
  } finally {
    callerSignal?.removeEventListener('abort', abort);
    session.closedSignal.removeEventListener('abort', abort);
  }
}

export async function listRecoveries(): Promise<RecoverySummary[]> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction([META, SNAPSHOTS], 'readonly');
    const metadata = await requestResult(transaction.objectStore(META).getAll()) as RecoveryMeta[];
    const snapshots = transaction.objectStore(SNAPSHOTS);
    const result: RecoverySummary[] = [];
    for (const item of metadata) {
      const current = await requestResult(snapshots.get(item.currentKey)) as RecoveryRecord | undefined;
      const previous = item.previousKey ? await requestResult(snapshots.get(item.previousKey)) as RecoveryRecord | undefined : undefined;
      if (current) result.push({ sessionId: current.sessionId, documentId: current.documentId, documentName: current.documentName, revision: current.revision, savedAt: current.savedAt, key: current.key, generation: 'current' });
      if (previous) result.push({ sessionId: previous.sessionId, documentId: previous.documentId, documentName: previous.documentName, revision: previous.revision, savedAt: previous.savedAt, key: previous.key, generation: 'previous' });
    }
    await transactionDone(transaction);
    return result.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  } finally {
    database.close();
  }
}

async function readRecoveryRecord(database: IDBDatabase, key: string): Promise<RecoveryRecord | undefined> {
  const transaction = database.transaction(SNAPSHOTS, 'readonly');
  const value = await requestResult(transaction.objectStore(SNAPSHOTS).get(key)) as RecoveryRecord | undefined;
  await transactionDone(transaction);
  return value;
}

async function touchRecoveryRecord(database: IDBDatabase, key: string): Promise<void> {
  const transaction = database.transaction(SNAPSHOTS, 'readwrite');
  await withWriteTransaction(transaction, async () => {
    const store = transaction.objectStore(SNAPSHOTS);
    const record = await requestResult(store.get(key)) as RecoveryRecord | undefined;
    // A load may finish after deletion or budget eviction. Never recreate its stale record.
    if (record) store.put({ ...record, lastAccessedAt: new Date().toISOString() });
  });
}

export async function loadRecovery(key: string, signal?: AbortSignal): Promise<import('../core/model').EditorDocument> {
  throwIfAborted(signal);
  const database = await openDatabase();
  let candidate: import('../core/model').EditorDocument | undefined;
  let delivered = false;
  const readCandidate = async (bytes: Uint8Array) => {
    const result = await deserializeProject(bytes, signal);
    await prepareNativeDocumentResources(result.document, signal);
    candidate = result.document;
    return result;
  };
  try {
    let record = await readRecoveryRecord(database, key);
    if (!record) throw new Error('Recovery snapshot no longer exists.');
    let result: Awaited<ReturnType<typeof deserializeProject>>;
    let usedFallback = false;
    try {
      result = await readCandidate(new Uint8Array(record.bytes));
    } catch (currentError) {
      throwIfAborted(signal);
      const metaRead = database.transaction(META, 'readonly');
      const metadata = await requestResult(metaRead.objectStore(META).get(record.sessionId)) as RecoveryMeta | undefined;
      await transactionDone(metaRead);
      const fallbackKey = metadata?.currentKey === record.key ? metadata.previousKey : undefined;
      if (!fallbackKey) throw currentError;
      record = await readRecoveryRecord(database, fallbackKey);
      if (!record) throw currentError;
      result = await readCandidate(new Uint8Array(record.bytes));
      usedFallback = true;
    }
    // Access-time bookkeeping is best effort. A write failure is not archive
    // corruption and must never replace the validated current document with an older one.
    await touchRecoveryRecord(database, record.key).catch(() => undefined);
    result.document.source = { kind: 'recovery', name: record.documentName };
    if (usedFallback) {
      result.document.importReport = {
        codec: 'Maipo recovery',
        sourceName: record.documentName,
        notices: [...(result.document.importReport?.notices ?? []), { severity: 'warning', feature: 'recovery-generation', message: 'The newest recovery generation could not be loaded. Maipo opened the previous validated generation.', handling: 'preserved' }],
      };
    }
    throwIfAborted(signal);
    delivered = true;
    return result.document;
  } finally {
    if (candidate && !delivered) releaseNativeDocumentResources(candidate);
    database.close();
  }
}

export async function deleteRecovery(sessionId: string): Promise<void> {
  const database = await openDatabase();
  try {
    // Read generation pointers and delete their snapshots in one transaction;
    // a concurrent autosave must not change those pointers between read and delete.
    const transaction = database.transaction([META, SNAPSHOTS], 'readwrite');
    await withWriteTransaction(transaction, async () => {
      const metadataStore = transaction.objectStore(META);
      const metadata = await requestResult(metadataStore.get(sessionId)) as RecoveryMeta | undefined;
      if (metadata) {
        metadataStore.delete(sessionId);
        transaction.objectStore(SNAPSHOTS).delete(metadata.currentKey);
        if (metadata.previousKey) transaction.objectStore(SNAPSHOTS).delete(metadata.previousKey);
      }
    });
  } finally {
    database.close();
  }
}
